import { useRef, useState } from "react";
import type { TenantBranding } from "@/api/chain";
import { chainApi, tenantApi } from "@/api/chain";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { resolveTenantLogo } from "@/lib/tenantBranding";
import { toast } from "sonner";
import { Upload } from "lucide-react";

export type LogoUploadTarget =
  | { kind: "group"; groupId: string }
  | { kind: "hospital"; hospitalId: string };

type Props = {
  value: TenantBranding;
  onChange: (next: TenantBranding) => void;
  /** When set, shows file upload (requires saved tenant/hospital id). */
  logoUpload?: LogoUploadTarget;
  /** Show hint that empty hospital fields inherit from tenant */
  inheritHint?: boolean;
};

const colorFields: {
  key: "primaryColor" | "secondaryColor" | "accentColor" | "sidebarBgColor";
  label: string;
}[] = [
  { key: "primaryColor", label: "Primary (header, nav & page titles)" },
  { key: "secondaryColor", label: "Secondary" },
  { key: "accentColor", label: "Accent" },
  { key: "sidebarBgColor", label: "Sidebar background" },
];

export function TenantBrandingFields({ value, onChange, logoUpload, inheritHint }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const set = <K extends keyof TenantBranding>(key: K, v: TenantBranding[K]) =>
    onChange({ ...value, [key]: v });

  const handleLogoFile = async (file: File | undefined) => {
    if (!file || !logoUpload) return;
    setUploading(true);
    try {
      const res =
        logoUpload.kind === "group"
          ? await tenantApi.uploadGroupLogo(logoUpload.groupId, file)
          : await chainApi.uploadHospitalLogo(logoUpload.hospitalId, file);
      const logoUrl = res.data?.data?.logoUrl as string | undefined;
      if (!logoUrl) throw new Error("Upload did not return logo URL");
      set("logoUrl", logoUrl);
      toast.success("Logo uploaded");
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err.response?.data?.message || err.message || "Logo upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const revertToDefaultUi = () => {
    onChange({ ...value, customUiColors: false });
    toast.success("Reverted to default UI colors (logo unchanged)");
  };

  return (
    <div className="space-y-4 rounded-lg border bg-muted/30 p-4">
      <div>
        <p className="text-sm font-medium">Branding</p>
        {inheritHint && (
          <p className="text-xs text-muted-foreground mt-1">
            Leave colors empty to inherit tenant defaults. Logo URL is optional.
          </p>
        )}
        {!inheritHint && (
          <p className="text-xs text-muted-foreground mt-1">
            Default UI uses the system color palette; only the logo is tenant-specific.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="branding-logo-url">Logo</Label>
        {logoUpload ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
              className="hidden"
              onChange={(e) => void handleLogoFile(e.target.files?.[0])}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? "Uploading…" : "Upload logo"}
            </Button>
            <span className="text-xs text-muted-foreground">PNG, JPG, WEBP, GIF, SVG · max 2 MB</span>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Save the tenant first, then upload a logo when editing.
          </p>
        )}
        <Label htmlFor="branding-logo-url" className="text-xs text-muted-foreground font-normal">
          Or paste logo URL
        </Label>
        <Input
          id="branding-logo-url"
          placeholder="https://… or /uploads/tenant-branding/…"
          value={value.logoUrl ?? ""}
          onChange={(e) => set("logoUrl", e.target.value)}
        />
        <div className="flex items-center gap-3 pt-1">
          <span className="text-xs text-muted-foreground">Preview</span>
          <img
            src={resolveTenantLogo(value)}
            alt="Logo preview"
            className="h-10 max-w-[160px] object-contain rounded border bg-white px-2"
            onError={(e) => {
              (e.target as HTMLImageElement).src = resolveTenantLogo(null);
            }}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-background px-3 py-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id="branding-custom-colors"
            checked={value.customUiColors}
            onCheckedChange={(v) => set("customUiColors", !!v)}
          />
          <Label htmlFor="branding-custom-colors" className="text-sm font-normal cursor-pointer">
            Use custom brand colors
          </Label>
        </div>
        {value.customUiColors && (
          <Button type="button" variant="outline" size="sm" onClick={revertToDefaultUi}>
            Revert to default UI
          </Button>
        )}
      </div>

      <div
        className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${!value.customUiColors ? "opacity-50 pointer-events-none" : ""}`}
      >
        {colorFields.map(({ key, label }) => (
          <div key={key} className="space-y-1">
            <Label htmlFor={`branding-${key}`}>{label}</Label>
            <div className="flex gap-2">
              <Input
                id={`branding-${key}`}
                type="color"
                className="w-12 h-10 p-1 cursor-pointer"
                value={(value[key] as string) || "#154D92"}
                onChange={(e) => set(key, e.target.value)}
                disabled={!value.customUiColors}
              />
              <Input
                value={value[key] ?? ""}
                onChange={(e) => set(key, e.target.value)}
                placeholder="#154D92"
                className="font-mono text-sm"
                disabled={!value.customUiColors}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
